import { RequestStatusBadge } from "@/components/common/section-components/badge/RequestStatusBadge";
import {
  fetchTaxesUploadHistoryHandler,
  type UploadUiStatus,
  uploadTaxesFileHandler,
  validateFile,
} from "@/pages/config/taxes/schema/handler-functions";
import type { TaxUploadHistoryItem } from "@/pages/config/taxes/schema/taxes.schema";
import { AppColors } from "@/styles/colors";
import { TaxesUploadStatus } from "@/constants/entity-status";
import { PermissionCode } from "@/constants/permissions";
import { Box, Flex, Text } from "@chakra-ui/react";

import { useEffect, useMemo, useRef, useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { useRoles } from "@/hooks/useRoles";
import { TaxesErrorsModal } from "./TaxesErrorsModal";
import { uploadBoxCss } from "../../schema/custom-styles";
import { UploadBoxContent } from "./UploadBoxContent";
import { HistoryContent } from "./HistoryContent";
import { UTActionElements } from "./UTActionElements";

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

interface Props {
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  setCurrentTab: (tab: string) => void;
}

const buildStatusBadge = (status: string) => {
  console.log(status);
  return <RequestStatusBadge status={status as TaxesUploadStatus} />;
};

export const UploadTaxesTab = ({
  navigate,
  fetchPermissions,
  setCurrentTab,
}: Props) => {
  const { hasWriteAccess } = useRoles();
  const canUploadTaxes = hasWriteAccess(PermissionCode.TAXES_LOAD);

  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [processingFileName, setProcessingFileName] = useState<string | null>(
    null,
  );

  const [lastUploadedFileName, setLastUploadedFileName] = useState<
    string | null
  >(null);
  const [uploadUiStatus, setUploadUiStatus] = useState<UploadUiStatus>("idle");
  const [uploadResultMessage, setUploadResultMessage] = useState<string | null>(
    null,
  );

  const [loadingHistory, setLoadingHistory] = useState(false);
  const [history, setHistory] = useState<TaxUploadHistoryItem[]>([]);

  const [openErrors, setOpenErrors] = useState(false);
  const [selectedForDetail, setSelectedForDetail] =
    useState<TaxUploadHistoryItem | null>(null);

  const uploadTaxesHeaders = useMemo(
    () => [
      { label: "Fecha", value: "fecha", align: "inherit" },
      { label: "Archivo", value: "archivo", align: "inherit" },
      { label: "Estado", value: "estadoBadge", align: "inherit" },
      { label: "Detalle", value: "detalle", align: "inherit" },
    ],
    [],
  );

  const openFilePicker = () => {
    // Reset value so selecting the same file again triggers onChange
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    inputRef.current?.click();
  };

  useEffect(() => {
    if (isActive) {
      fetchTaxesUploadHistoryHandler({
        setLoading: setLoadingHistory,
        setHistory,
        navigate,
        fetchPermissions,
        showLoad: true,
      });
    }
  }, [isActive]);

  const handleFileSelected = (file: File | null | undefined) => {
    if (!file) return;

    const err = validateFile(file, MAX_FILE_SIZE_BYTES);
    if (err) {
      setLastUploadedFileName(file.name);
      setUploadUiStatus("invalid");
      setUploadResultMessage(err);
      return;
    }

    uploadTaxesFileHandler({
      file,
      setUploadUiStatus,
      setUploadResultMessage,
      setProcessingFileName,
      setLastUploadedFileName,
      setHistory,
    });
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    handleFileSelected(file);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const handleOpenErrors = (row: TaxUploadHistoryItem) => {
    setSelectedForDetail(row);
    setOpenErrors(true);
  };

  const getActionElements = (item: any) => {
    const row = item as TaxUploadHistoryItem;

    if (!row?.errores?.length) return <></>;

    return <UTActionElements handleOpenErrors={handleOpenErrors} row={row} />;
  };

  const uploadBoxBg = (() => {
    if (uploadUiStatus === "processing") return AppColors.BACKGROUND_SECONDARY;
    if (uploadUiStatus === "invalid" || uploadUiStatus === "recordErrors") {
      return "#FEE2E2";
    }
    if (uploadUiStatus === "success") return "#DCFCE7";
    if (isDragging) return "#E6FFFA";
    return AppColors.WHITE_SECONDARY;
  })();

  return (
    <>
      <Flex flexDir="column" gap="12px">
        {canUploadTaxes && (
          <Box
            borderRadius="8px"
            border={`1px dashed ${AppColors.WHITE_BORDER_1}`}
            bgColor={uploadBoxBg}
            width="100%"
            css={uploadBoxCss}
            marginX="auto"
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => {
                handleFileSelected(e.target.files?.[0]);
                // Ensure next selection (even same file) triggers onChange
                e.currentTarget.value = "";
              }}
            />

            <Flex
              flexDir="column"
              alignItems="center"
              justifyContent="center"
              gap="10px"
            >
              <UploadBoxContent
                uploadUiStatus={uploadUiStatus}
                processingFileName={processingFileName}
                lastUploadedFileName={lastUploadedFileName}
                uploadResultMessage={uploadResultMessage}
                history={history}
                setSelectedForDetail={setSelectedForDetail}
                setOpenErrors={setOpenErrors}
                openFilePicker={openFilePicker}
                setProcessingFileName={setProcessingFileName}
                setLastUploadedFileName={setLastUploadedFileName}
                setUploadResultMessage={setUploadResultMessage}
                setUploadUiStatus={setUploadUiStatus}
                setCurrentTab={setCurrentTab}
              />
            </Flex>
          </Box>
        )}

        <Flex flexDir="column" gap="12px" paddingTop="10px">
          <Text fontWeight={700} fontSize="16px">
            Historial de cargas
          </Text>

          <Box width="100%">
            <HistoryContent
              loadingHistory={loadingHistory}
              history={history}
              uploadTaxesHeaders={uploadTaxesHeaders}
              buildStatusBadge={buildStatusBadge}
              getActionElements={getActionElements}
            />
          </Box>
        </Flex>
      </Flex>

      <TaxesErrorsModal
        open={openErrors}
        setOpen={(flag) => {
          setOpenErrors(flag);
          if (!flag) setSelectedForDetail(null);
        }}
        selectedForDetail={selectedForDetail}
      />
    </>
  );
};
